<template>
  <b-modal
    :active.sync="showModal"
    :width="450"
  >
    <form
      class="form"
      @submit.prevent="login"
    >
      <div
        class="modal-card"
        style="width: auto"
      >
        <header class="modal-card-head is-block">
          <span class="icon-static xl large-user" />
          <h4 class="title is-4">
            Login to Sonata
          </h4>
        </header>
        <section class="modal-card-body">
          <div
            v-for="(err, i) in formErrors.non_field_errors"
            :key="i"
            class="message is-danger has-text-left"
          >
            <div class="message-body">
              {{ err }}
            </div>
          </div>
          <div class="field">
            <label class="label">Email address</label>
            <div class="control">
              <input
                id="id_email"
                v-model="email"
                type="email"
                name="email"
                autofocus
                class="input"
                required
                :disabled="busy"
                placeholder="Email address"
              >
            </div>
          </div>
          <div class="field">
            <label class="label">Password</label>
            <div class="control">
              <input
                id="id_password"
                v-model="password"
                type="password"
                name="password"
                class="input"
                required
                :disabled="busy"
                placeholder="Password"
              >
            </div>
            <p class="help">
              <a
                class="text medium"
                @click.prevent="handleShowPasswordReset"
              >
                Forgotten your password? Reset it
              </a>
            </p>
          </div>
        </section>
        <footer class="modal-card-foot is-block">
          <button
            v-if="false"
            type="button"
            class="button is-block is-secondary"
            @click.prevent="$store.dispatch('auth/hideLoginModal')"
          >
            Close
          </button>
          <button
            type="submit"
            class="button is-fullwidth is-primary margin-bottom is-large"
            :class="{ 'is-loading': busy }"
            :disabled="busy"
          >
            Login
          </button>

          <a
            class="text medium"
            @click.prevent="handleShowSignup"
          >
            Don't have an account? Sign up
          </a>
        </footer>
      </div>
    </form>
  </b-modal>
</template>

<script>
import { identify as segmentIdentify } from '@/utils/segment.js'
import { identify as logrocketIdentify } from '@/utils/logrocket.js'

export default {
  name: 'TheLoginModal',

  data () {
    return {
      email: '',
      password: '',
      formErrors: {},
      busy: false
    }
  },

  computed: {
    showModal: {
      get: function () {
        return this.$store.getters['auth/showLoginModal']
      },
      set: function (newValue) {
        if (newValue === true) {
          this.$store.dispatch('auth/showLoginModal')
        } else {
          // Closed by the user without logging in, so forget where they were headed.
          // Otherwise a later login would send them back to that page.
          this.$store.dispatch('auth/hideLoginModal')
          this.$store.dispatch('auth/clearRedirectTo')
        }
      }
    }
  },

  methods: {
    login () {
      this.formErrors = {}
      this.busy = true

      this.$store.dispatch('auth/login', {
        email: this.email,
        password: this.password
      })
        .then(
          () => this.completeLogin(),
          (error) => this.handleLoginError(error)
        )
        .finally(() => {
          this.busy = false
        })
    },

    completeLogin () {
      // The credentials were accepted, so the user is logged in from here on. If loading
      // their account fails, still close the modal rather than report a failed login.
      return this.$store.dispatch('common/initAuthenticated')
        .then(() => this.identify())
        .finally(() => {
          const authRedirectTo = this.$store.getters['auth/redirectTo']
          this.reset()
          this.$store.dispatch('auth/hideLoginModal')
          this.$store.dispatch('auth/clearRedirectTo')

          if (authRedirectTo) {
            this.$router.push(authRedirectTo)
          } else if (this.$route.name !== 'track-search') {
            // Only redirect if we're not on the track search page.
            this.$router.push({ name: 'track-search' })
          }
        })
    },

    handleLoginError (error) {
      if (error.response && error.response.status === 401) {
        this.password = ''
        this.$set(this, 'formErrors', { non_field_errors: [error.response.data.detail] })
        return
      }
      const errors = { non_field_errors: ['Something went wrong logging in. Please try again.'] }
      this.$set(this, 'formErrors', errors)
      return Promise.reject(error.response || error)
    },

    identify () {
      const user = this.$store.getters['user/currentUser']
      const team = this.$store.getters['user/currentTeam']
      const currentSubscription = this.$store.getters['subscriptions/currentSubscription']

      const userTraits = {
        email: user.email,
        name: (user.first_name + ' ' + user.last_name).trim(),
        team: team.name
      }
      if (currentSubscription) {
        const subscriptionTraits = {
          subscription_status: currentSubscription.status,
          subscription_plan: currentSubscription.plan.product_name,
          subscription_interval: currentSubscription.plan.interval,
          subscription_currency: currentSubscription.plan.currency
        }
        Object.assign(userTraits, subscriptionTraits)
      }

      segmentIdentify(user.pk, userTraits)
      logrocketIdentify(user.pk, userTraits)
    },

    reset () {
      this.formErrors = {}
      this.email = ''
      this.password = ''
    },

    handleShowSignup () {
      this.$store.dispatch('auth/hideLoginModal')
      this.$store.dispatch('user/showSignupModal')
    },

    handleShowPasswordReset () {
      this.$store.dispatch('auth/hideLoginModal')
      this.$store.dispatch('auth/showPasswordResetModal')
    }
  }
}
</script>

<style>

</style>
